import type {
  PlayerSnapshot,
  PlayerState,
  PlayerToTvMessage,
  TvToPlayerMessage,
  VideoSummary,
} from "./types";

export interface YouTubePlayerBridgeOptions {
  frame: HTMLIFrameElement;
  overlay: HTMLElement;
  shellUrl: string;
  onSnapshot: (snapshot: PlayerSnapshot) => void;
  onError: (message: string) => void;
  onNotice?: (message: string) => void;
}

interface PendingVideo {
  videoId: string;
  startSeconds: number;
}

const PLAYER_STATES: readonly PlayerState[] = [
  "IDLE",
  "LOADING",
  "PLAYING",
  "PAUSED",
  "ENDED",
  "ERROR",
];

export class YouTubePlayerBridge {
  private readonly frame: HTMLIFrameElement;
  private readonly overlay: HTMLElement;
  private readonly shellOrigin: string;
  private readonly bridgeToken = randomBridgeToken();
  private readonly onSnapshot: (snapshot: PlayerSnapshot) => void;
  private readonly onError: (message: string) => void;
  private readonly onNotice: (message: string) => void;
  private ready = false;
  private opened = false;
  private pendingVideo: PendingVideo | null = null;
  private currentVideo: VideoSummary | null = null;
  private snapshot: PlayerSnapshot = idleSnapshot();
  private readyTimer: number | null = null;

  constructor(options: YouTubePlayerBridgeOptions) {
    this.frame = options.frame;
    this.overlay = options.overlay;
    this.onSnapshot = options.onSnapshot;
    this.onError = options.onError;
    this.onNotice = options.onNotice ?? options.onError;

    const shell = validShellUrl(options.shellUrl);
    this.shellOrigin = shell.origin;
    shell.searchParams.set("bridgeToken", this.bridgeToken);
    shell.searchParams.set("parentOrigin", window.location.origin);
    shell.searchParams.set("appVersion", __KIDTUBE_APP_VERSION__);

    this.frame.src = shell.toString();
    this.frame.title = "Trình phát YouTube của KidTube";
    this.frame.setAttribute(
      "sandbox",
      "allow-scripts allow-same-origin allow-presentation",
    );
    this.frame.setAttribute("allow", "autoplay; encrypted-media; fullscreen; picture-in-picture");
    window.addEventListener("message", this.handleMessage);
    this.startReadyTimeout();
  }

  open(video: VideoSummary): void {
    this.currentVideo = video;
    this.pendingVideo = {
      videoId: video.videoId,
      startSeconds: Math.max(0, Math.floor(video.progressSeconds ?? 0)),
    };
    this.opened = true;
    this.overlay.hidden = false;
    this.overlay.setAttribute("aria-hidden", "false");
    this.updateSnapshot({
      ...idleSnapshot(),
      state: "LOADING",
      positionSeconds: this.pendingVideo.startSeconds,
      durationSeconds: video.durationSeconds,
    });
    if (this.ready) {
      this.sendPendingVideo();
    }
  }

  close(): void {
    if (this.opened) {
      this.post({
        source: "KIDTUBE_TV",
        version: 1,
        bridgeToken: this.bridgeToken,
        type: "STOP",
      });
    }
    this.opened = false;
    this.pendingVideo = null;
    this.currentVideo = null;
    this.overlay.hidden = true;
    this.overlay.setAttribute("aria-hidden", "true");
    this.updateSnapshot(idleSnapshot());
  }

  play(): boolean {
    return this.sendControl("PLAY");
  }

  pause(): boolean {
    return this.sendControl("PAUSE");
  }

  seekTo(positionSeconds: number): boolean {
    if (!this.opened || !this.ready || !Number.isFinite(positionSeconds)) {
      return false;
    }
    const target = clampSeconds(positionSeconds);
    this.post({
      source: "KIDTUBE_TV",
      version: 1,
      bridgeToken: this.bridgeToken,
      type: "SEEK",
      positionSeconds: target,
    });
    this.updateSnapshot({ ...this.snapshot, positionSeconds: target });
    return true;
  }

  setPlaybackRate(playbackRate: number): boolean {
    if (!this.opened || !this.ready || !validPlaybackRate(playbackRate)) {
      return false;
    }
    this.post({
      source: "KIDTUBE_TV",
      version: 1,
      bridgeToken: this.bridgeToken,
      type: "SET_PLAYBACK_RATE",
      playbackRate,
    });
    return true;
  }

  setCaptions(enabled: boolean): boolean {
    if (!this.opened || !this.ready) {
      return false;
    }
    this.post({
      source: "KIDTUBE_TV",
      version: 1,
      bridgeToken: this.bridgeToken,
      type: "SET_CAPTIONS",
      enabled,
    });
    return true;
  }

  /**
   * Đổi chất lượng phát. Chỉ gửi mức player shell đã báo là khả dụng,
   * "auto" luôn được phép. Không tạo resolution giả.
   */
  setQuality(quality: string): boolean {
    if (!this.opened || !this.ready) {
      return false;
    }
    const available = this.snapshot.availableQualityLevels ?? [];
    if (quality !== "auto" && available.indexOf(quality) < 0) {
      return false;
    }
    this.post({
      source: "KIDTUBE_TV",
      version: 1,
      bridgeToken: this.bridgeToken,
      type: "SET_QUALITY",
      quality,
    });
    return true;
  }

  stop(): boolean {
    if (!this.opened) {
      return false;
    }
    this.close();
    return true;
  }

  isOpen(): boolean {
    return this.opened;
  }

  activeVideo(): VideoSummary | null {
    return this.currentVideo;
  }

  currentSnapshot(): PlayerSnapshot {
    return { ...this.snapshot };
  }

  destroy(): void {
    window.removeEventListener("message", this.handleMessage);
    if (this.readyTimer !== null) {
      window.clearTimeout(this.readyTimer);
      this.readyTimer = null;
    }
  }

  private readonly handleMessage = (event: MessageEvent<unknown>): void => {
    if (event.source !== this.frame.contentWindow || event.origin !== this.shellOrigin) {
      return;
    }
    const message = parsePlayerMessage(event.data, this.bridgeToken);
    if (message === null) {
      return;
    }

    if (message.type === "READY") {
      this.ready = true;
      if (this.readyTimer !== null) {
        window.clearTimeout(this.readyTimer);
        this.readyTimer = null;
      }
      this.sendPendingVideo();
      return;
    }

    if (message.type === "ERROR") {
      this.updateSnapshot({ ...this.snapshot, state: "ERROR" });
      this.onError(message.message || "Không thể phát video này trên TV.");
      return;
    }

    if (message.type === "NOTICE") {
      this.onNotice(message.message);
      return;
    }

    if (message.type !== "STATE") {
      return;
    }

    this.updateSnapshot({
      state: message.state,
      positionSeconds: clampSeconds(message.positionSeconds),
      durationSeconds: clampSeconds(message.durationSeconds),
      ...(message.bufferedFraction === undefined ? {} : { bufferedFraction: clampFraction(message.bufferedFraction) }),
      ...(message.playbackRate === undefined ? {} : { playbackRate: message.playbackRate }),
      ...(message.availablePlaybackRates === undefined ? {} : { availablePlaybackRates: [...message.availablePlaybackRates] }),
      ...(message.quality === undefined ? {} : { quality: message.quality }),
      ...(message.availableQualityLevels === undefined ? {} : { availableQualityLevels: [...message.availableQualityLevels] }),
      ...(message.captionsAvailable === undefined ? {} : { captionsAvailable: message.captionsAvailable }),
      ...(message.captionsEnabled === undefined ? {} : { captionsEnabled: message.captionsEnabled }),
    });
  };

  private sendPendingVideo(): void {
    if (!this.ready || !this.opened || this.pendingVideo === null) {
      return;
    }
    this.post({
      source: "KIDTUBE_TV",
      version: 1,
      bridgeToken: this.bridgeToken,
      type: "LOAD",
      videoId: this.pendingVideo.videoId,
      startSeconds: this.pendingVideo.startSeconds,
    });
    this.pendingVideo = null;
  }

  private sendControl(type: "PLAY" | "PAUSE"): boolean {
    if (!this.opened || !this.ready) {
      return false;
    }
    this.post({
      source: "KIDTUBE_TV",
      version: 1,
      bridgeToken: this.bridgeToken,
      type,
    });
    return true;
  }

  private post(message: TvToPlayerMessage): void {
    this.frame.contentWindow?.postMessage(message, this.shellOrigin);
  }

  private updateSnapshot(snapshot: PlayerSnapshot): void {
    this.snapshot = snapshot;
    this.onSnapshot({ ...snapshot });
  }

  private startReadyTimeout(): void {
    this.readyTimer = window.setTimeout(() => {
      this.readyTimer = null;
      if (!this.ready) {
        this.onError(
          "Trình phát YouTube chưa sẵn sàng. Hãy kiểm tra mạng hoặc cập nhật phần mềm TV.",
        );
      }
    }, 15_000);
  }
}

function validShellUrl(value: string): URL {
  let parsed: URL;
  try {
    parsed = new URL(value);
  } catch {
    throw new Error("KidTube player shell URL không hợp lệ.");
  }
  if (parsed.protocol !== "https:" && parsed.protocol !== "http:") {
    throw new Error("KidTube player shell phải dùng HTTP hoặc HTTPS.");
  }
  return parsed;
}

function randomBridgeToken(): string {
  const bytes = new Uint8Array(24);
  window.crypto.getRandomValues(bytes);
  let token = "";
  for (let index = 0; index < bytes.length; index += 1) {
    const hex = (bytes[index] ?? 0).toString(16);
    token += hex.length === 1 ? `0${hex}` : hex;
  }
  return token;
}

function parsePlayerMessage(data: unknown, bridgeToken: string): PlayerToTvMessage | null {
  if (!isRecord(data)) {
    return null;
  }
  if (
    data.source !== "KIDTUBE_PLAYER" ||
    data.version !== 1 ||
    data.bridgeToken !== bridgeToken
  ) {
    return null;
  }
  if (data.type === "READY") {
    return {
      source: "KIDTUBE_PLAYER",
      version: 1,
      bridgeToken,
      type: "READY",
    };
  }
  if (
    data.type === "STATE" &&
    isPlayerState(data.state) &&
    finiteNumber(data.positionSeconds) &&
    finiteNumber(data.durationSeconds) &&
    optionalFiniteNumber(data.bufferedFraction) &&
    optionalFiniteNumber(data.playbackRate) &&
    optionalNumberArray(data.availablePlaybackRates) &&
    optionalString(data.quality) &&
    optionalStringArray(data.availableQualityLevels) &&
    optionalBoolean(data.captionsAvailable) &&
    optionalBoolean(data.captionsEnabled)
  ) {
    return {
      source: "KIDTUBE_PLAYER",
      version: 1,
      bridgeToken,
      type: "STATE",
      state: data.state,
      positionSeconds: data.positionSeconds,
      durationSeconds: data.durationSeconds,
      ...(data.bufferedFraction === undefined ? {} : { bufferedFraction: data.bufferedFraction }),
      ...(data.playbackRate === undefined ? {} : { playbackRate: data.playbackRate }),
      ...(data.availablePlaybackRates === undefined ? {} : { availablePlaybackRates: [...data.availablePlaybackRates] }),
      ...(data.quality === undefined ? {} : { quality: data.quality }),
      ...(data.availableQualityLevels === undefined ? {} : { availableQualityLevels: [...data.availableQualityLevels] }),
      ...(data.captionsAvailable === undefined ? {} : { captionsAvailable: data.captionsAvailable }),
      ...(data.captionsEnabled === undefined ? {} : { captionsEnabled: data.captionsEnabled }),
    };
  }
  if ((data.type === "NOTICE" || data.type === "ERROR") && typeof data.code === "string" && typeof data.message === "string") {
    return {
      source: "KIDTUBE_PLAYER",
      version: 1,
      bridgeToken,
      type: data.type,
      code: data.code,
      message: data.message,
    };
  }
  return null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isPlayerState(value: unknown): value is PlayerState {
  return typeof value === "string" && PLAYER_STATES.indexOf(value as PlayerState) >= 0;
}

function finiteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function optionalFiniteNumber(value: unknown): value is number | undefined {
  return value === undefined || finiteNumber(value);
}

function optionalNumberArray(value: unknown): value is number[] | undefined {
  return value === undefined || (Array.isArray(value) && value.every(finiteNumber));
}

function optionalStringArray(value: unknown): value is string[] | undefined {
  return (
    value === undefined ||
    (Array.isArray(value) && value.every((item) => typeof item === "string"))
  );
}

function optionalString(value: unknown): value is string | undefined {
  return value === undefined || typeof value === "string";
}

function optionalBoolean(value: unknown): value is boolean | undefined {
  return value === undefined || typeof value === "boolean";
}

function validPlaybackRate(value: number): boolean {
  return Number.isFinite(value) && value >= 0.25 && value <= 2;
}

function clampSeconds(value: number): number {
  return Math.max(0, Math.min(86_400, value));
}

function clampFraction(value: number): number {
  return Math.max(0, Math.min(1, value));
}

function idleSnapshot(): PlayerSnapshot {
  return { state: "IDLE", positionSeconds: 0, durationSeconds: 0 };
}
